/**
 * editor-text-templates.js
 */

import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import Icon from 'components/icon';


const useStyles = makeStyles(theme => ({
  useButton: {
    paddingTop: 0,
    paddingBottom: 0,
    fontSize: theme.typography.sizes[0],
    '&:hover': {
      backgroundColor: theme.palette.sys.surfaceContainerHighest
    }, 
  },
  titleIcon: {
    marginRight: theme.spacing.dialogTitleIconMargin,
    marginBottom: theme.spacing(-0.5),
  },
  list: {
    margin: `0 -${theme.spacing(2)}`
  },
  header: {
    backgroundColor: theme.palette.background.paper,
  },
  listItem: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  optionText: {
    whiteSpace: 'pre-wrap'
  }
}));


export default function EditorTextTemplates(props) {
  const {
    recent,
    translation,
    samplesTranslationKey,
    onSelect,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation(translation);
  const [recentOptions, setRecentOptions] = React.useState();
  const [open, setOpen] = React.useState(false);


  React.useEffect(() => {
    if (recent) {
      setRecentOptions(recent.map((entry, i) => ({
        key: `recent-${i}`,
        text: entry
      })));
    }
  }, [recent]);


  const sampleOptions = React.useMemo(() => {
    if (samplesTranslationKey) {
      return t(samplesTranslationKey, { returnObjects: true }).map((entry, i) => ({
        key: `sample-${i}`,
        text: entry
      }));
    }
  }, [samplesTranslationKey, t]);


  const handleSelect = (option) => {
    onSelect(option.text);
    handleClose();
  }


  const handleClose = () => {
    setOpen(false);
  }


  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          className={classes.useButton}
          color="primary"
          size="small"
          onClick={() => setOpen(true)}
          data-testid="template-button"
        >
          Use Template
        </Button>
      </div>

      <Dialog
        open={open}
        onClose={handleClose}
        classes={{ paper: classes.root }}
        aria-labelledby="choose-template-title"
        aria-describedby="choose-template-content"
        keepMounted={true}
        style={{ zIndex: 1310 }} // position above drawer and tour dialog
      >
        <DialogTitle>
          <Icon className={classes.titleIcon} type="description"/>
          Choose Template
        </DialogTitle>

        <DialogContent style={{ paddingTop: 0 }}>
          <Typography variant='subtext-large' sx={{ my: 2 }}>
            Select a <strong>Recent Entry</strong> or a pre-written <strong>Sample</strong> to append to this field.
          </Typography>

          <List component="nav" className={classes.list} subheader={<li />}>

            {/* recent entries */}
            {recentOptions &&
            <div key="recent-entries">
              <ListSubheader className={classes.header}>Recent Entries</ListSubheader>
              {recentOptions.map(option => (
                <ListItem
                  key={option.key}
                  button
                  className={classes.listItem}
                  onClick={() => handleSelect(option)}
                >
                  <ListItemText className={classes.optionText} primary={option.text} />
                </ListItem>
              ))}
            </div>}

            {/* samples */}
            {sampleOptions &&
            <div key="samples">
              <ListSubheader className={classes.header}>Samples</ListSubheader>
              {sampleOptions.map(option => (
                <ListItem
                  key={option.key}
                  button
                  className={classes.listItem}
                  onClick={() => handleSelect(option)}
                >
                  <ListItemText className={classes.optionText} primary={option.text} />
                </ListItem>
              ))}
            </div>}

          </List>

        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>
            Cancel
          </Button>
        </DialogActions>

      </Dialog>
    </>
  )
}